import { Controller } from "@hotwired/stimulus"

const PROMPT_TEMPLATE = `I need you to research and generate a CSV of real, verified activities for visitors to LOCATION_PLACEHOLDER and the surrounding area. Use your web_search tool to verify each place actually exists and is currently open. Do NOT rely on your training data alone.

VERIFICATION PROCESS (MANDATORY for every place):
1. For each place you want to include, run web_search for "[business name] [city]"
2. Look at the top 5 search results. Does the EXACT business name appear in any result title or snippet?
3. If YES — use the name exactly as it appears in search results (not your assumed name)
4. If NO — the place probably doesn't exist or has a different name. DO NOT INCLUDE IT. Move on.
5. Example: if you search "Oliver Farmers Market Oliver BC" and results show "Station Street Market" instead, do NOT add "Oliver Farmers Market". Either add "Station Street Market" (the real name) or skip it.

CRITICAL:
- Do NOT rely on your training data for business names. Your training data frequently has wrong names, closed businesses, and merged/renamed places.
- If search results show a DIFFERENT name than what you expected, that means your name is WRONG. Use the real name or skip it.
- Never assume a place exists just because the name sounds plausible. SEARCH FIRST, then decide.

OTHER RULES:
- INCLUDE activities in nearby towns and surrounding areas that visitors would reasonably visit. DO NOT remove places just because they are in a neighbouring town.
- For each activity, set the location_path to the ACTUAL city/town where the place is located, not the search city. For example, a restaurant in Lantzville gets location_path "canada/british-columbia/lantzville", NOT "canada/british-columbia/nanaimo".
- Write descriptions as standalone — do NOT reference the search city or distances from it. For example, do NOT write "30 minutes from [city]" or "a short drive from [city]". The activity may appear on pages for multiple nearby cities.
- Descriptions should be 2-4 sentences of practical, honest, traveler-helpful info. Include real pitfalls and logistics. No fluff.
- Include real street addresses. For trailheads and natural areas, use the parking lot or access point address.
- Include latitude/longitude for every entry — especially important for trailheads and natural areas that may not have a street address.
- Do NOT invent websites or phone numbers — leave those blank.
- Generate COUNT_PLACEHOLDER diverse activities. Cast a wide net — include restaurants, hikes, parks, beaches, wineries, family spots, hidden gems, and anything a visitor would enjoy.

CATEGORIES (use ONLY these values):
dining, hiking, parks, beaches, water_activities, outdoor_recreation, shopping, nightlife, family, tours, entertainment, wineries

BEST_FOR (use ONLY these values, pipe-separated):
Families, Couples, Solo travelers, Groups, Kids, Photographers, Foodies, Adventure seekers

WHAT_TO_BRING (use ONLY these values, pipe-separated):
Water, Sunscreen, Hat, Hiking shoes, Comfortable shoes, Camera, Snacks, Cash, Swimsuit, Towel, Jacket, Binoculars, Insect repellent, Backpack

PRICE_RANGE: Free, $, $$, $$$, or $$$$

KNOW_BEFORE_YOU_GO: Practical logistical warnings — cash only, book ahead, seasonal closures, accessibility notes, etc. Pipe-separated.

OUTPUT FORMAT — CSV with these exact headers:
location_path,name,category,description,hours,price_range,address,duration,latitude,longitude,tips,best_for,what_to_bring,know_before_you_go

LOCATION_PATH FORMAT: Use slugified hierarchy with slashes.
Example: canada/british-columbia/nanaimo or united-states/california/malibu

MULTI-VALUE FIELDS: Use pipe (|) to separate multiple values in tips, best_for, what_to_bring, and know_before_you_go.

EXAMPLE ROW:
canada/british-columbia/nanaimo,Neck Point Park,parks,"Rocky coastline park with tide pools and stunning views of the Gulf Islands. The loop trail is easy and flat. Seals and sea lions are often spotted from the rocky outcrops.",6am - 11pm,Free,"1055 Morningside Dr, Nanaimo, BC",1-2 hours,49.2352,-123.9659,Visit at low tide for the best tide pools|The loop trail is about 3km and mostly flat|Parking lot fills up on summer weekends,Families|Couples|Photographers,Comfortable shoes|Camera|Binoculars,Dogs must be on leash|No facilities — bring your own water|Some trails are slippery when wet

EXCLUDE_PLACEHOLDER
Generate the full CSV now for: LOCATION_PLACEHOLDER`

export default class extends Controller {
  static targets = ["input", "count", "output", "panel", "copyButton"]
  static values = { existingUrl: String }

  async generate() {
    const location = this.inputTarget.value.trim()
    if (!location) return

    const count = this.countTarget.value || "30"
    const existing = await this.fetchExisting(location)

    let prompt = PROMPT_TEMPLATE
      .replaceAll("LOCATION_PLACEHOLDER", location)
      .replace("COUNT_PLACEHOLDER", count)

    if (existing.length > 0) {
      const list = existing.map(n => `- ${n}`).join("\n")
      prompt = prompt.replace("EXCLUDE_PLACEHOLDER",
        `ALREADY IMPORTED (skip these — they are already in our system):\n${list}\n`)
    } else {
      prompt = prompt.replace("EXCLUDE_PLACEHOLDER\n", "")
    }

    this.outputTarget.textContent = prompt
    this.panelTarget.classList.remove("hidden")
    this.copyButtonTarget.textContent = "Copy Prompt"
  }

  copy() {
    navigator.clipboard.writeText(this.outputTarget.textContent).then(() => {
      this.copyButtonTarget.textContent = "Copied!"
      setTimeout(() => { this.copyButtonTarget.textContent = "Copy Prompt" }, 2000)
    })
  }

  async fetchExisting(location) {
    try {
      const url = `${this.existingUrlValue}?location=${encodeURIComponent(location)}`
      const response = await fetch(url)
      if (!response.ok) return []
      return await response.json()
    } catch {
      return []
    }
  }
}
